import { motion } from "framer-motion";
import { Users, Briefcase, Car } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { whatsappUrl } from "../../lib/leads";

const FLEET = [
  {
    name: "Swift Dzire",
    type: "Sedan",
    seats: 4,
    bags: 2,
    note: "Comfy & economical for city rides",
    img: "/assets/swift.webp",
    span: "sm:col-span-1",
  },
  {
    name: "Ertiga",
    type: "MUV · 6+1",
    seats: 6,
    bags: 3,
    note: "Ideal for small families",
    img: "/assets/ertiga.webp",
    span: "sm:col-span-1",
  },
  {
    name: "Innova Crysta",
    type: "Premium MUV",
    seats: 7,
    bags: 4,
    note: "The outstation favourite",
    img: "/assets/crysta.webp",
    span: "sm:col-span-2",
  },
  {
    name: "Fortuner",
    type: "Luxury SUV",
    seats: 7,
    bags: 4,
    note: "Arrive in style & power",
    img: "/assets/fortuner.webp",
    span: "sm:col-span-2",
  },
  {
    name: "Urbania",
    type: "Luxury Van · 13+",
    seats: 13,
    bags: 10,
    note: "Group tours & pilgrimages",
    img: "/assets/urbania.webp",
    span: "sm:col-span-1",
  },
];

const FALLBACK = "https://images.pexels.com/photos/35644782/pexels-photo-35644782.jpeg?auto=compress&cs=tinysrgb&w=900&q=70";

const CarCard = ({ car, i }) => (
  <motion.a
    href={whatsappUrl(`Namaste! I'd like to book the ${car.name}.`)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`fleet-card-${car.name}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-3xl bg-white border border-jodhpur-primary/10 ${car.span}`}
  >
    <div className="relative h-56 sm:h-64 overflow-hidden bg-jodhpur-primary/5">
      {car.img ? (
        <img
          src={car.img}
          alt={car.name}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
      ) : (
        <div className="h-full w-full flex flex-col items-center justify-center bg-gradient-to-br from-jodhpur-primary to-jodhpur-secondary text-white/90">
          <Car className="h-14 w-14 mb-2 transition-transform duration-500 group-hover:scale-110" />
          <span className="text-sm font-medium tracking-wide">{car.name}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-jodhpur-ink/70 via-transparent to-transparent" />
      <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-semibold text-jodhpur-primary">
        {car.type}
      </span>
    </div>
    <div className="p-5 sm:p-6 flex items-end justify-between">
      <div>
        <h3 className="font-display text-2xl font-semibold text-jodhpur-primary">{car.name}</h3>
        <p className="text-sm text-jodhpur-ink/60 mt-1">{car.note}</p>
        <div className="mt-3 flex items-center gap-4 text-jodhpur-ink/70 text-sm">
          <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {car.seats}</span>
          <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {car.bags}</span>
        </div>
      </div>
      <span className="shrink-0 rounded-full border border-jodhpur-primary/20 px-4 py-2 text-xs font-semibold text-jodhpur-primary transition-colors group-hover:bg-jodhpur-primary group-hover:text-white">
        Book →
      </span>
    </div>
  </motion.a>
);

export const Fleet = () => {
  return (
    <section id="fleet" data-testid="fleet-section" className="relative bg-jodhpur-bg py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <RevealOnScroll>
              <span className="text-xs uppercase tracking-[0.3em] text-jodhpur-accent font-semibold">Our Fleet</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-jodhpur-primary tracking-tight max-w-xl">
                A car for every <span className="italic">journey</span>
              </h2>
            </RevealOnScroll>
          </div>
          <RevealOnScroll delay={0.1}>
            <p className="max-w-sm text-jodhpur-ink/65">
              Handpicked, well-maintained vehicles with professional chauffeurs — from budget sedans to luxury vans.
            </p>
          </RevealOnScroll>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FLEET.map((car, i) => (
            <CarCard key={car.name} car={car} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
